import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { addToChainAfter, executeAwaitableHandlerChain, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { CatalogPage } from "@insite/client-framework/Services/CategoryService";
import {
    GetProductCollectionApiV2Parameter,
    ProductExpandTokens,
} from "@insite/client-framework/Services/ProductServiceV2";
import { getSearchDataModeActive } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    DispatchLoadProducts,
    PopulateApiParameter,
    GetUnfilteredProducts,
    SetExpandTokens,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";

export interface GetProductCollectionApiV2ParameterExtended extends GetProductCollectionApiV2Parameter {
    includeProductsInSubCategories?: boolean;
}

export interface DisplayProductsResult {
    products?: ProductModel[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
    productFilters: ProductFilters;
    isFiltered: boolean;
    unfilteredApiParameter?: GetProductCollectionApiV2Parameter;
    filteredApiParameter?: GetProductCollectionApiV2Parameter;
    catalogPage?: CatalogPage;
}

interface Parameter {
    queryString?: string;
    path: string;
    isSearch: boolean;
    query?: string;
    onException?: (error: unknown) => void;
}

interface Props {
    apiParameter: GetProductCollectionApiV2ParameterExtended;
    idByPath?: SafeDictionary<string>;
    result: DisplayProductsResult;
    pricingLoaded?: true;
    expandTokens?: ProductExpandTokens;
}

export const ParseQueryParameterCategories: HandlerType = props => {
    const statevalue = props.getState() as CustomApplicationState;

    const subCategoriesFlag = statevalue.t_components.CENCustomCategoriesFilterPropertiesData.isIncludeSubCategoriesFlag;
    if(props.apiParameter && props.apiParameter.categoryId){
         props.apiParameter = {
            ...props.apiParameter,
            includeProductsInSubCategories: subCategoriesFlag,
        }
    }
    if (props.apiParameter) {
        props.apiParameter.expand = props.apiParameter?.expand || [];
        props.apiParameter?.expand?.push("properties");
    }

    const currentPage = props.getState().pages.productList.parameter?.page || 1;
    if (props.apiParameter.page === currentPage + 1) {
        props.apiParameter.pageToken = getProductListDataViewProperty(props.getState(), "pagination")?.nextPageToken;
    }
};

export const DispatchLoadProductsExtended: HandlerType = async props => {
    const state = props.getState();
    props.result.products = getProductsDataView(state, props.apiParameter).value;
    if (props.result.products) {
        const attributeTypeFacetIds = getProductListDataViewProperty(state, "attributeTypeFacets")?.map(
            facet => facet.attributeTypeId,
        );
        const attributeTypeIds: string[] = [];
        props.result.products.forEach(product => {
            const ids = product.attributeTypes?.map(attributeType => attributeType.id) || [];
            attributeTypeIds.push(...ids);
        });
        if (attributeTypeIds.length === attributeTypeFacetIds?.length) {
            const allAttributesAssignedToCategory = attributeTypeIds.every(
                id => attributeTypeFacetIds.indexOf(id) > -1,
            );
            if (allAttributesAssignedToCategory) {
                return;
            }
        }
    }

    const onError = props.parameter.onException
        ? (error: string) => {
              throw new Error(error);
          }
        : undefined;
    try {
        props.result.products = await executeAwaitableHandlerChain(
            loadProducts,
            {
                ...props.apiParameter,
                isLoadMore: isLoadMore(props.getState()) && !!props.apiParameter.page && props.apiParameter.page > 1,
                ...(onError ? { onError } : {}),
            },
            props,
        );
    } catch (error) {
        if (!props.parameter.onException) {
            throw error;
        }
        props.parameter.onException(error);
        return false;
    }
};


export const CenSetExpandTokensExtended: HandlerType = props => {
    props.expandTokens = ["attributes", "facets", "badges"];
    if (getSearchDataModeActive(props.getState())) {
        props.expandTokens.push("scoreexplanation");
    }
};

export const GetUnfilteredProductsExtended: HandlerType = async props => {
    const unfilteredApiParameter = { ...props.apiParameter };
    delete unfilteredApiParameter.searchWithin;
    if (!props.result.productFilters.pageBrandId) {
        delete unfilteredApiParameter.brandIds;
    }
    if (!props.result.productFilters.pageProductLineId) {
        delete unfilteredApiParameter.productLineIds;
    }
    delete unfilteredApiParameter.priceFilters;
    delete unfilteredApiParameter.attributeValueIds;
    if (!props.result.productFilters.pageCategoryId) {
        delete unfilteredApiParameter.categoryId;
        delete unfilteredApiParameter.includeProductsInSubCategories;
    }
    unfilteredApiParameter.stockedItemsOnly = undefined;
    unfilteredApiParameter.previouslyPurchasedProducts = undefined;

    props.result.unfilteredApiParameter = unfilteredApiParameter;
    props.result.filteredApiParameter = { ...props.apiParameter };

    const unfilteredProducts = getProductsDataView(props.getState(), unfilteredApiParameter).value;
    if (unfilteredProducts) {
        return;
    }

    const onError = props.parameter.onException
        ? (error: string) => {
              throw new Error(error);
          }
        : undefined;
    try {
        await executeAwaitableHandlerChain(
            loadProducts,
            {
                ...unfilteredApiParameter,
                ...(onError ? { onError } : {}),
            },
            props,
        );
    } catch (error) {
        if (!props.parameter.onException) {
            throw error;
        }
        props.parameter.onException(error);
        return false;
    }
};

type HandlerType = Handler<Parameter, Props>;

addToChainAfter(chain, PopulateApiParameter, ParseQueryParameterCategories);
replaceInChain(chain, DispatchLoadProducts, DispatchLoadProductsExtended);
replaceInChain(chain,SetExpandTokens,CenSetExpandTokensExtended);
replaceInChain(chain,GetUnfilteredProducts,GetUnfilteredProductsExtended);